import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const GIFTBOX_SYNC_SOURCE = 'hog-giftbox-sync';
/** Poll delays (ms) after a user cart mutation — Gift Box may mutate the cart asynchronously. */
const GIFTBOX_POLL_DELAYS = [300, 700, 1200];

/** @type {AbortController | null} */
let giftboxPollAbort = null;

/** @type {boolean} */
let checkoutGuardBound = false;

/**
 * @param {{ items?: Array<{ key: string, quantity: number }> }} cart
 * @returns {string}
 */
function cartFingerprint(cart) {
  if (!cart?.items?.length) return '';
  return cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
}

/**
 * @param {{ handle?: string, title?: string, product_title?: string }} item
 * @returns {boolean}
 */
function isGiftBoxLine(item) {
  const handle = item.handle || '';
  const title = item.title || item.product_title || '';
  return handle.startsWith('free-gift-') || title.startsWith('FREE GIFT');
}

/**
 * @param {{ items?: Array, items_subtotal_price?: number }} cart
 * @returns {boolean}
 */
function isGiftOnlyCart(cart) {
  if (!cart?.items?.length) return false;
  if (cart.items_subtotal_price !== 0) return false;
  return cart.items.every(isGiftBoxLine);
}

/**
 * @returns {string}
 */
function cartJsUrl() {
  return `${Theme.routes.cart_url}.js`;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#bindCheckoutGuard();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Bind once (drawer + cart page may both mount this component).
   */
  #bindCheckoutGuard() {
    if (checkoutGuardBound) return;
    checkoutGuardBound = true;

    document.addEventListener('click', this.#onCheckoutClick, true);
    document.addEventListener('submit', this.#onCartFormSubmit, true);
  }

  /**
   * @param {MouseEvent} event
   */
  #onCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutButton = target.closest('#checkout, .cart__checkout-button');
    if (!(checkoutButton instanceof HTMLElement)) return;
    if (checkoutButton.hasAttribute('disabled')) return;

    event.preventDefault();
    event.stopPropagation();
    this.#guardGiftOnlyCheckout();
  };

  /**
   * @param {SubmitEvent} event
   */
  #onCartFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    const submitter = event.submitter;
    const isCheckoutSubmit =
      submitter instanceof HTMLElement &&
      (submitter.getAttribute('name') === 'checkout' ||
        submitter.id === 'checkout' ||
        submitter.classList.contains('cart__checkout-button'));

    // Keyboard / implicit submit without a checkout submitter — still guard.
    if (!isCheckoutSubmit && submitter != null) return;

    event.preventDefault();
    event.stopPropagation();
    this.#guardGiftOnlyCheckout();
  };

  /**
   * Block checkout when the cart only contains Gift Box free gifts.
   */
  async #guardGiftOnlyCheckout() {
    try {
      const response = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        this.#proceedToCheckout();
        return;
      }

      const cart = await response.json();

      if (!isGiftOnlyCart(cart)) {
        this.#proceedToCheckout();
        return;
      }

      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of cart.items) {
        if (isGiftBoxLine(item)) updates[item.key] = 0;
      }

      await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates }) }));

      document.dispatchEvent(
        new CartUpdateEvent(cart, GIFTBOX_SYNC_SOURCE, {
          itemCount: 0,
          source: GIFTBOX_SYNC_SOURCE,
          sections: {},
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  #proceedToCheckout() {
    const form = document.getElementById('cart-form');
    if (!(form instanceof HTMLFormElement)) {
      window.location.href = '/checkout';
      return;
    }

    // Native checkout submit bypasses submit listeners (no re-entry into the guard).
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'checkout';
    input.value = '';
    form.appendChild(input);
    HTMLFormElement.prototype.submit.call(form);
  }

  /**
   * After a user cart mutation, poll /cart.js so Gift Box add/remove appears without a full reload.
   * @param {string} [baselineFingerprint]
   */
  #scheduleGiftBoxSync(baselineFingerprint = '') {
    giftboxPollAbort?.abort();
    const abortController = new AbortController();
    giftboxPollAbort = abortController;

    const baseline = baselineFingerprint;
    let attempt = 0;

    const runPoll = async () => {
      if (abortController.signal.aborted) return;
      if (attempt >= GIFTBOX_POLL_DELAYS.length) return;

      const delay = GIFTBOX_POLL_DELAYS[attempt];
      attempt += 1;

      await new Promise((resolve) => {
        const timer = setTimeout(resolve, delay);
        abortController.signal.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            resolve(undefined);
          },
          { once: true }
        );
      });

      if (abortController.signal.aborted) return;

      try {
        const response = await fetch(cartJsUrl(), {
          headers: { Accept: 'application/json' },
          signal: abortController.signal,
        });
        if (!response.ok) {
          runPoll();
          return;
        }

        const cart = await response.json();
        const nextFingerprint = cartFingerprint(cart);

        if (nextFingerprint !== baseline) {
          giftboxPollAbort = null;
          document.dispatchEvent(
            new CartUpdateEvent(cart, GIFTBOX_SYNC_SOURCE, {
              itemCount: cart.item_count ?? 0,
              source: GIFTBOX_SYNC_SOURCE,
              sections: {},
            })
          );
          return;
        }

        runPoll();
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error(error);
      }
    };

    runPoll();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        this.#scheduleGiftBoxSync(cartFingerprint(parsedResponseText));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const source = event.detail?.data?.source;
    const isGiftBoxSync = source === GIFTBOX_SYNC_SOURCE;

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // Gift Box mutates the cart after Horizon's first render — poll once per user action.
    // Skip when this event is our own sync re-render.
    if (!isGiftBoxSync) {
      const baseline = cartFingerprint(event.detail?.resource ?? {});
      this.#scheduleGiftBoxSync(baseline);
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
